import { useEffect, useState } from 'react';
import { FORM_CONTENT_TYPE, hcMetadataStatus, visibilitySelectionGlossaries } from 'src/commons/constants';
import { Page } from 'src/routes';
import { editGlossary, getGlossary } from 'src/api/catalog';
import * as React from 'react';
import { Redirect, useLocation } from 'react-router-dom';
import {
  Button,
  ColumnLayout,
  Container,
  Flashbar,
  Form,
  FormField,
  Header,
  Input,
  Select,
  SpaceBetween,
} from '@amzn/awsui-components-react-v3';
import {
  GOVERNANCE_TAG_TYPE,
  glossaryTypeDisplayName,
  glossaryTypeDisplayNameLowerCase,
  USER_GLOSSARY_TYPE,
} from 'src/components/metadata/constants';
import { findOption } from 'src/components/catalog/common';
import { getGlossariesForOwner } from 'src/components/workspaces/common/common';

export interface EditBusinessGlossaryProps {
  setContentType: any;
  location: any;
  match: any;
  activeGroup: string;
  activeWorkspace: any;
  glossarySubType?: string;
}

const EditBusinessGlossary = (props: EditBusinessGlossaryProps) => {
  const [redirect, setRedirect] = useState(undefined);
  const [notifications, setNotifications] = useState([]);
  const [glossaryId, setGlossaryId] = useState(undefined);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [ownerId, setOwnerId] = useState(undefined);
  const [glossaryOwnerId, setGlossaryOwnerId] = useState(undefined);
  const [status, setStatus] = useState(null);
  const [description, setDescription] = useState(undefined);
  const [glossaryValue, setGlossaryValue] = useState(undefined);
  const [name, setName] = useState(undefined);
  const [visibility, setVisibility] = useState(undefined);
  const [glossaryType, setGlossaryType] = useState(undefined);
  const [glossarySubTypeName, setGlossarySubTypeName] = useState('');
  const [glossarySubTypeNameLC, setGlossarySubTypeNameLC] = useState('');
  const [isGovernanceTag, setIsGovernanceTag] = useState(false);
  const [parentGlossaryId, setParentGlossaryId] = useState(undefined);
  const [globalGlossaryOptions, setGlobalGlossaryOptions] = useState([]);
  const { state } = useLocation();

  useEffect(() => {
    props.setContentType(FORM_CONTENT_TYPE);
    const activeGroup = props.activeGroup;
    const activeWorkspace = props.activeWorkspace;
    const ownerId = activeWorkspace ? activeWorkspace.workspaceId : activeGroup;
    setOwnerId(ownerId);
    setGlossaryOwnerId(state?.glossaryOwnerId);
    setGlossaryId(state?.glossaryId);
  }, []);

  useEffect(() => {
    if (glossaryOwnerId && glossaryId) {
      fetchGlossaryToEdit();
    }
  }, [glossaryId, glossaryOwnerId]);

  const getGlossaryOptions = async (ownerId: string, glossarySubType: string) => {
    setButtonLoading(true);
    try {
      const glossaries = await getGlossariesForOwner(ownerId, glossarySubType);
      let globalGlossaryOptions = glossaries.map((gl) => {
        return { label: gl.GlossaryValue, value: gl.GlossaryId };
      });
      setGlobalGlossaryOptions(globalGlossaryOptions);
      return globalGlossaryOptions;
    } catch (err) {
      setNotification('error', `Unable to load all global ${glossarySubTypeName}. ${err.message}`);
    } finally {
      setButtonLoading(false);
    }
  };

  const fetchGlossaryToEdit = async () => {
    try {
      let getGlossaryResult = await getGlossary({
        OwnerId: glossaryOwnerId,
        GlossaryId: glossaryId,
      });
      let glossaryDetails = getGlossaryResult.GlossaryDetails;
      if (!glossaryDetails) {
        setNotification('error', 'No glossary found with id:' + glossaryId);
      } else {
        setDescription(glossaryDetails.Description);
        setName(glossaryDetails.Name);
        setGlossaryValue(glossaryDetails.GlossaryValue);
        setGlossaryType(glossaryDetails.GlossaryType);
        setGlossarySubTypeName(glossaryTypeDisplayName.get(glossaryDetails.GlossarySubType));
        setGlossarySubTypeNameLC(glossaryTypeDisplayNameLowerCase.get(glossaryDetails.GlossarySubType));
        setParentGlossaryId(glossaryDetails.ParentGlossaryId);
        const isGovernanceTag = GOVERNANCE_TAG_TYPE === glossaryDetails.GlossarySubType;
        setIsGovernanceTag(isGovernanceTag);
        if (isGovernanceTag) {
          const globalGlossaryOptionsLocal = await getGlossaryOptions(ownerId, glossaryDetails.GlossarySubType);
          setParentGlossaryId(findOption(globalGlossaryOptionsLocal, glossaryDetails.ParentGlossaryId));
        }
        setStatus(findOption(hcMetadataStatus, glossaryDetails.Status));
        setVisibility(findOption(visibilitySelectionGlossaries, glossaryDetails.Visibility));
      }
    } catch (err) {
      setNotification('error', err.message);
    }
  };

  const handleEditGlossary = async () => {
    setButtonLoading(true);
    try {
      const isUserGovernanceTag = isGovernanceTag && USER_GLOSSARY_TYPE === glossaryType;
      await editGlossary({
        GlossaryId: glossaryId,
        OwnerId: glossaryOwnerId,
        NewDescription: description,
        NewName: isGovernanceTag ? undefined : name,
        NewGlossaryValue: glossaryValue,
        NewStatus: status.value,
        Visibility: visibility.value,
        ParentGlossaryId: isUserGovernanceTag ? parentGlossaryId?.value : undefined,
      });
      setRedirect({
        pathname: isGovernanceTag ? Page.GOVERNANCE_TAGS : Page.BUSINESS_GLOSSARIES,
        state: {
          status: 'success',
          message: `${glossarySubTypeName} updated successfully`,
        },
      });
    } catch (e) {
      setNotification('error', `Error when trying to edit ${glossarySubTypeNameLC}: ${e.message}`);
    } finally {
      setButtonLoading(false);
      setDescription(undefined);
      setStatus(null);
      setName(undefined);
    }
  };

  const setNotification = async (header, message) => {
    if (header === 'error') {
      setNotifications([
        {
          header: header,
          type: 'error',
          content: message,
          dismissible: true,
          onDismiss: () => setNotifications([]),
        },
      ]);
    } else {
      setNotifications([
        {
          type: 'success',
          content: message,
          dismissible: true,
          onDismiss: () => setNotifications([]),
        },
      ]);
    }
  };

  return (
    <div>
      {redirect && <Redirect push to={redirect} />}
      <div id='flash-bar'>
        <Flashbar items={notifications} />
      </div>
      <Form
        header={
          <Header variant='h1' description={`Provide the details to edit ${glossarySubTypeNameLC}.`}>
            Edit {glossarySubTypeNameLC}
          </Header>
        }
        actions={
          <SpaceBetween direction='horizontal' size='s'>
            <Button
              variant='link'
              onClick={() => setRedirect(isGovernanceTag ? Page.GOVERNANCE_TAGS : Page.BUSINESS_GLOSSARIES)}
            >
              Cancel
            </Button>
            <Button variant='primary' loading={buttonLoading} onClick={handleEditGlossary}>
              {'Save changes'}
            </Button>
          </SpaceBetween>
        }
      >
        <Container header={<Header variant='h2'>{glossarySubTypeName} details</Header>}>
          <ColumnLayout>
            <SpaceBetween size='m'>
              <FormField
                label={<div>{glossarySubTypeName} ID</div>}
                description={`Unique identifier for ${glossarySubTypeNameLC}`}
              >
                <Input name='glossaryId' value={glossaryId} ariaRequired={true} disabled={true} />
              </FormField>
              <FormField label={<div>Owner Id</div>} description={`${glossarySubTypeName} owner`}>
                <Input name='ownerId' value={glossaryOwnerId} ariaRequired={true} disabled={true} />
              </FormField>
              {!isGovernanceTag && (
                <FormField label={<div>Name</div>} description={`Name of ${glossarySubTypeNameLC}.`}>
                  <Input
                    name='name'
                    value={name}
                    ariaRequired={true}
                    onChange={({ detail }) => setName(detail.value)}
                  />
                </FormField>
              )}
              <FormField
                label={<div>{isGovernanceTag ? 'Governance tag name' : 'Value'}</div>}
                description={`${isGovernanceTag ? 'Name of governance tag' : 'Value of business glossary'}.`}
              >
                <Input
                  name='value'
                  value={glossaryValue}
                  onChange={({ detail }) => setGlossaryValue(detail.value.trim())}
                  ariaRequired={true}
                />
              </FormField>
              <FormField label={<div>Description</div>} description={`Description of ${glossarySubTypeNameLC}.`}>
                <Input
                  name='description'
                  value={description}
                  ariaRequired={true}
                  onChange={({ detail }) => setDescription(detail.value)}
                />
              </FormField>
              {isGovernanceTag && USER_GLOSSARY_TYPE === glossaryType && (
                <FormField
                  label={<div>Parent tag</div>}
                  description={`Select a parent tag. A Global parent tag is required for User tag.`}
                >
                  <Select
                    name='parentGlossaryId'
                    selectedOption={parentGlossaryId}
                    ariaRequired={true}
                    placeholder='Choose a parent governance tag'
                    onChange={({ detail }) => setParentGlossaryId(detail.selectedOption)}
                    options={globalGlossaryOptions}
                    selectedAriaLabel='Selected'
                  />
                </FormField>
              )}
              <FormField
                label='Status'
                description={`Status of ${glossarySubTypeNameLC}. Only Enabled entities can be associated to resources!`}
              >
                <Select
                  name='status'
                  placeholder='Enabled'
                  selectedOption={status}
                  options={hcMetadataStatus}
                  ariaRequired={true}
                  onChange={({ detail }) => {
                    setStatus(detail.selectedOption);
                  }}
                  disabled={false}
                />
              </FormField>
              <FormField label='Visibility' description={`Visibility of ${glossarySubTypeNameLC}.`}>
                <Select
                  name='visibility'
                  placeholder='Public'
                  selectedOption={visibility}
                  options={visibilitySelectionGlossaries}
                  ariaRequired={true}
                  onChange={({ detail }) => {
                    setVisibility(detail.selectedOption);
                  }}
                  disabled={false}
                />
              </FormField>
            </SpaceBetween>
          </ColumnLayout>
        </Container>
      </Form>
    </div>
  );
};

export default EditBusinessGlossary;
